/**
 * NewsletterSignupBlock — the reusable double-opt-in signup form.
 *
 * Encapsulates the email field, submit, and the two local states (the form and
 * the post-submit "check your email" confirmation). Both the standalone
 * NewsletterSignupPage and the storefront promo popup render this so the signup
 * behaviour stays in one place (DRY).
 *
 * Targeting: pass `orgSlug` (storefront list) or `ownerUserId` (legacy per-creator
 * list); omit both for the global platform newsletter. The server resolves the
 * master org (Sleepy Circuits) and the plain landing page to the same global pool.
 */

import { useState } from 'react';
import { subscribeToNewsletter } from '../../services/newsletterSignupService';
import { EMAIL_REGEX } from '../../sleepy-ui/authPageModel';


export interface NewsletterSignupBlockProps {
  /** Storefront org slug — routes to that org's list. */
  orgSlug?: string;
  /** Legacy per-creator target. */
  ownerUserId?: number;
  headline?: string;
  subtext?: string;
  /** Show the "link expired, request a fresh one" hint above the form. */
  confirmError?: boolean;
  /** Extra content under the button (e.g. the "full account" pill). */
  footer?: React.ReactNode;
  autoFocus?: boolean;
  /** Compact spacing/colors for the popup; default is the full-page look. */
  compact?: boolean;
  /** Called after a successful submit (the email that was entered). */
  onSubmitted?: (email: string) => void;
  /** The signup's state when the caller holds it (useNewsletterSignup); else the block's own. */
  signup?: NewsletterSignup;
}

export type NewsletterSignup = ReturnType<typeof useNewsletterSignup>;

/**
 * The signup's state and submit (the block's, and NewsletterSignupPage's
 * when sleepy-ui draws it): the email, the busy / sent states, the error.
 */
export function useNewsletterSignup({ orgSlug, ownerUserId, onSubmitted }: Pick<NewsletterSignupBlockProps,
  'orgSlug' | 'ownerUserId' | 'onSubmitted'>) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const normalized = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(normalized)) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const result = await subscribeToNewsletter(normalized, { orgSlug, ownerUserId });
    setSubmitting(false);
    if (result.success) {
      setSubmitted(true);
      onSubmitted?.(normalized);
    } else {
      setError(result.error || 'Something went wrong. Please try again.');
    }
  };
  // The email input as the browser's autofill sees it.
  const emailInput = {
    type: 'email', name: 'email', autoComplete: 'email', required: true, placeholder: 'you@example.com', value: email,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => { setEmail(e.target.value); setError(null); },
  };
  return { email, submitting, submitted, error, handleSubmit, emailInput };
}

export function NewsletterSignupBlock({
  orgSlug,
  ownerUserId,
  headline = 'Join the mailing list',
  subtext = "Get occasional updates. We'll email you a link to confirm.",
  confirmError = false,
  footer,
  autoFocus = false,
  compact = false,
  onSubmitted,
  signup,
}: NewsletterSignupBlockProps) {
  const own = useNewsletterSignup({ orgSlug, ownerUserId, onSubmitted });
  const { email, submitting, submitted, error, handleSubmit, emailInput } = signup ?? own;

  // ── Post-submit ("check your email") ─────────────────────────────────────
  if (submitted) {
    return (
      <div className={`flex flex-col gap-3 text-center ${compact ? '' : 'w-80'}`}>
        <h2 className="text-lg font-semibold text-zinc-200">Check your email</h2>
        <p className="text-zinc-400 text-sm">
          We sent a confirmation link to <span className="text-white">{email}</span>.
          Click it to finish subscribing. The link expires in 7 days.
        </p>
        {footer && <div className="pt-1">{footer}</div>}
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────
  return (
    <form onSubmit={handleSubmit} className={`flex flex-col gap-4 ${compact ? '' : 'w-80'}`}>
      <div>
        <h2 className="text-xl font-semibold text-zinc-200">{headline}</h2>
        <p className="text-zinc-500 text-sm mt-1">{subtext}</p>
      </div>

      {confirmError && (
        <p className="text-amber-400 text-sm">
          That confirmation link was invalid or has expired. Enter your email to get a fresh one.
        </p>
      )}

      <div className="flex flex-col gap-1">
        <label className="text-zinc-400 text-xs font-medium">Email</label>
        <input
          {...emailInput}
          autoFocus={autoFocus}
          className="bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white outline-none focus:border-zinc-500 text-sm"
        />
      </div>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      <button
        type="submit"
        disabled={submitting}
        className="bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-white rounded px-4 py-2 transition-colors text-sm font-medium"
      >
        {submitting ? 'Subscribing…' : 'Subscribe'}
      </button>

      {footer && (
        <div className="pt-2 border-t border-zinc-800 mt-2 flex flex-col items-center gap-2">
          <span className="text-zinc-600 text-xs">or</span>
          {footer}
        </div>
      )}
    </form>
  );
}

export default NewsletterSignupBlock;
